<template>
  <div>
    <spinner-round :isLoading="loading" v-if="loading"></spinner-round>
    <sticky v-if="company && !loading" class="page-header sticky-page-header page-header--new" :back-button="true">
      <span slot="left">
        <h1 class="title is-3">
          <router-link :to="{name: 'CompanyList'}"
                       class="has-text-black is-uppercase">Sociétés /</router-link>
          <span class="has-text-weight-light has-text-black is-uppercase">{{company.name}}</span>
          <span v-if="isAdmin && company.disabled" class="tag is-small has-text-white is-danger">
              Société Désactivée
            </span>
        </h1>
      </span>
      <router-link :to="{name: 'CompanyEdit', params: {id: company.pk}}" class="dropdown-item" slot="dropdown-content" v-if="isAdmin">
        <span class="icon"><i class="fas fa-edit"></i></span>
        <span>Modifier fiche société</span>
      </router-link>
      <a @click.prevent.stop="disableCompany" class="dropdown-item" slot="dropdown-content" v-if="isAdmin">
        <span v-if="company.disabled" class="icon"><i class="fas fa-check-circle"></i></span>
        <span v-else class="icon"><i class="fas fa-minus-circle"></i></span>
        <span>{{company.disabled ? 'Activer' : 'Désactiver'}} société</span>
      </a>
      <a @click="deleteCompany" class="has-text-danger dropdown-item" slot="dropdown-content" v-if="isAdmin">
        <span class="icon"><i class="fas fa-trash-alt"></i></span>
        <span>Supprimer société</span>
      </a>
    </sticky>
    <div v-if="company && !loading"
      class="columns is-padding-left-on-btn-actions is-padding-right-on-btn-actions has-stickyColumn has-stickyColumn--left">
      <div class="column is-4 is-3-fullhd is-paddingless-left is-paddingless-right">
          <div class="card has-bg-white is-bordered">
            <div class="is-padding-top is-padding-left is-padding-right is-padding-bottom-small has-text-centered">
               <figure class="is-margin-bottom-xsmall image is-128x128 is-margin-auto has-picto">
                <div v-if="company.created_on"
                  class="picto tooltip is-tooltip-right"
                  :data-tooltip="createdOnTooltip">
                  <div class="dot is-large has-bg-light-darken-3 has-text-white is-border is-border--white is-inline-block is-vcentered has-text-centered">
                    <div class="icon"><i class="fas fa-info"></i></div>
                  </div>                  
                </div>
                <div class="is-border is-border-circle is-clipped">
                  <img v-if="company.picture" :src="company.picture|avatarBig">
                  <div v-else class="has-bg-light-darken-2 has-text-white is-clipped is-relative" style="width:126px; height:126px; line-height:126px">
                        <i class="is-absolutecenter fas fa-building fa-5x"></i>
                    </div>
                </div>
              </figure>
              <div class="title is-3 is-margin-top-xsmall is-marginless-bottom">
                <b>{{company.name}}</b>
              </div>
              <div class="is-size-5 has-text-weight-light" v-if="company.address.city">
                {{company.address.city}}
              </div>
            </div>
            <div class="columns is-multiline" v-if="isAdmin || isSectorManager || isManager">
              <div class="column is-12 columns is-column is-space-between">
                <div class="columns is-size-5 columns is-vcentered">
                  <div class="tags is-centered is-marginless column is-paddingless is-narrow">
                    <span v-if="company.disabled" class="tag is-danger is-uppercase is-marginless is-normal">
                      <i class="fas fa-user-lock is-padding-right-xsmall"></i> Désactivé
                    </span>
                    <span v-else-if="companyDisableState" class="tag is-dark is-uppercase is-marginless is-normal">
                      <i class="fas fa-user-chart is-padding-right-xsmall"></i> Inactif
                    </span>
                    <span v-else class="tag is-success is-uppercase is-marginless is-normal">
                      <i class="fas fa-user-chart is-padding-right-xsmall"></i> Actif
                    </span>
                  </div>
                  <div class="is-size-5 column is-margin-left-small is-paddingless">
                    <b>Dernière connexion :</b>
                    {{lastUsersConnection|lastConnection}}
                  </div>
                </div>
              </div>
            </div>
            <div class="columns is-multiline is-border-top is-padding-bottom">
              <div class="column is-12 is-paddingless-bottom is-padding-top">
                <div class="columns is-gapless">
                  <div class="column is-size-6 is-narrow w80p is-margin-right-xsmall"><b>Adresse :</b></div>
                  <a class="column is-size-6 is-underline" @click="openGoogleMap(company.address)" v-if="company.address">
                    <div v-if="company.address.line1">{{company.address.line1}}</div>
                    <div v-if="company.address.line2">{{company.address.line2}}</div>
                    <div>
                      <span v-if="company.address.zip_code">{{company.address.zip_code}}</span>  
                      <span v-if="company.address.city">{{company.address.city}}</span>
                      <i v-if="company.address.country && company.address.country.trim().toLowerCase() !== 'france'">{{company.address.country}}</i>
                    </div>
                  </a>
                </div>
              </div>
              <div class="column is-12 is-padding-top-xsmall is-paddingless-bottom" v-if="company.phone">
                <div class="columns is-gapless">
                  <div class="column is-size-6 is-narrow w80p is-margin-right-xsmall"><b>Téléphone :</b></div>
                  <div class="column is-size-6">
                    <span>{{company.phone|prettyPhone}}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="columns is-multiline is-border-top is-padding-bottom" v-if="company.person_in_charge_last_name || company.person_in_charge_first_name">
              <div class="column is-12 is-paddingless-bottom is-padding-top">
                <div class="columns is-gapless">
                  <div class="column is-size-6 is-12 is-narrow w80p is-margin-right-xsmall"><b>Référent société :
                    <span class="is-uppercase" v-if="company.person_in_charge_last_name">{{company.person_in_charge_last_name}}</span>
                    <span v-if="company.person_in_charge_first_name">{{company.person_in_charge_first_name}}</span></b>
                  </div>
                </div>
              </div>
              <div class="column is-12 is-padding-top-xsmall is-paddingless-bottom">
                <div class="columns is-gapless">
                  <div class="column is-size-6 is-narrow w80p is-margin-right-xsmall"><b>Mail :</b></div>
                  <div class="column is-size-6 is-underlign" v-if="company.person_in_charge_email">
                    <a class="is-underline" :href="`mailto:${company.person_in_charge_email}`">{{company.person_in_charge_email}}</a>
                  </div>
                </div>
              </div>
              <div class="column is-12 is-padding-top-xsmall is-paddingless-bottom">
                <div class="columns is-gapless">
                  <div class="column is-size-6 is-narrow w80p is-margin-right-xsmall"><b>Téléphone :</b></div>
                  <div class="column is-size-6" v-if="company.person_in_charge_phone">
                    <span>{{company.person_in_charge_phone|prettyPhone}}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="columns is-multiline is-border-top">
              <a v-if="isAdmin" @click="changePanel(PANELS.ACCESS)"
                class="column is-6 is-border-right is-border-dashed columns is-column is-space-between">
                <div class="has-text-grey is-size-6">Mandats en cours</div>
                <div class="is-margin-top-xsmall is-size-4">
                  <span v-if="accessesCount">{{accessesCount}}</span>
                  <span v-else>--</span>
                </div>
              </a>
              <div class="column is-6 is-border-dashed columns is-column is-space-between">
                <div class="has-text-grey is-size-6" v-if="displayRemuneration">Rémunération</div>
                <div class="is-margin-top-xsmall is-size-4" v-if="displayRemuneration">
                  <span v-if="company.remuneration">{{company.remuneration|prettyPrice}}%</span>
                  <span v-else>--</span>
                </div>
              </div>
              <a @click="statsEnabled && isAdmin ? changePanel(PANELS.STATS) : null"
                  class="column is-6 is-border-top is-border-dashed columns is-border-right is-border-dashed is-column is-space-between">
                <div class="has-text-grey is-size-6">Réservation en cours</div>
                <div class="is-margin-top-xsmall is-size-4">
                  <span v-if="salesStats && salesStats.reservationsCount">{{salesStats.reservationsCount}} </span>
                  <span v-else>--</span>
                </div>
              </a>
              <a @click="statsEnabled && isAdmin ? changePanel(PANELS.VENTES) : null"
                class="column is-6 is-border-top columns is-column is-space-between">
                <div class="has-text-grey is-size-6">Vente</div>
                <div class="is-margin-top-xsmall is-size-4">
                  <span v-if="salesCount">{{salesCount}}</span>
                  <span v-else>--</span>
                </div>
              </a>
            </div>
          </div>
        </div>
      <div class="column is-8 is-9-fullhd is-paddingless-right">
        <div class="has-bg-white is-border is-padding-bottom-xsmall">
          <div class="tabs is-separatedUnderlined is-marginless-bottom">
            <ul class="has-text-weight-medium">
              <li v-if="statsEnabled && (isAdmin || isSectorManager)" :class="{'is-active': panel === 'STATS'}">
                <a @click="changePanel(PANELS.STATS)">Statistiques</a>
              </li>
              <li :class="{'is-active': panel === 'USER'}"><a @click="changePanel(PANELS.USER)">Utilisateurs</a></li>
              <li :class="{'is-active': panel === 'ACCESS'}" v-if="isAdmin"><a @click="changePanel(PANELS.ACCESS)">Mandats</a></li>
              <li :class="{'is-active': panel === 'OPTION'}"><a @click="changePanel(PANELS.OPTION)">Options</a></li>
              <li :class="{'is-active': panel === 'VENTES'}"><a @click="changePanel(PANELS.VENTES)">Ventes</a></li>
            </ul>
          </div>
          <component :is="panelComponent"
                     :sales="sales"
                     :company="company"
                     :sales-stats-data="salesStats"
                     :previous-sales-stats-data="previousSalesStats">
          </component>
        </div>
        <company-charts-pane v-if="panel === 'STATS' && !loadingStats"
                             :sales-entities="entities"
        ></company-charts-pane>
      </div>
    </div>
  </div>
</template>

<script>
  import moment from 'moment'
  import { mapGetters } from 'vuex'
  import { DefaultValues, OptionStatus, StatsMx } from '../../mixins'
  import UserPanel from './CompanyVuePanels/UserPanel'
  import VentePanel from './CompanyVuePanels/VentePanel'
  import OptionPanel from './CompanyVuePanels/OptionPanel'
  import CompanyAccessPanel from './CompanyVuePanels/CompanyAccessPanel'
  import StatsPanel from './CompanyVuePanels/StatsPanel'
  import CompanyChartsPane from './CompanyVuePanels/CompanyChartsPane'
  import parallel from 'async/parallel'

  const PANELS = Object.freeze({
    USER: 'USER',
    OPTION: 'OPTION',
    ACCESS: 'ACCESS',
    STATS: 'STATS',
    VENTES: 'VENTES'
  })

  export default {
    name: 'CompanyVue',
    mixins: [DefaultValues, OptionStatus, StatsMx],
    components: {
      UserPanel,
      OptionPanel,
      CompanyAccessPanel,
      StatsPanel,
      CompanyChartsPane,
      VentePanel
    },
    data () {
      return {
        salesAll: [],
        company: null,
        loading: true,
        users: null,
        accessesCount: null,
        salesCount: null,
        options: null,
        entityKey: 'COMPANY',
        sales: [],
        previousSales: [],
        panel: PANELS.USER,
        PANELS: PANELS,
        loadingStats: true,
        startDate: null,
        endDate: null
      }
    },
    filters: {
      lastConnection (val) {
        if (val) {
          let d = moment(val)
          return d.format('DD/MM [à] HH:mm')
        } else {
          return 'AUCUNE'
        }
      }
    },
    watch: {
      '$route' (val, oldVal) {
        if (val && oldVal && val.params.id !== oldVal.params.id) {
          this.initComponent()
        }
      },
      accessCurrentPage (to) {
        this.getAccess(to)
      },
      optionCurrentPage (to) {
        this.getOptions(to)
      }
    },
    computed: {
      ...mapGetters(['isAdmin', 'isManager', 'isSectorManager', 'currentUserPk', 'isDistributeurMode', 'displayRemuneration']),
      entities () {
        let tmpDict = {}
        let addKey = function (entityPk, name) {
          tmpDict[entityPk] = {
            data: {
              sales: [],
              rdvs: []
            },
            pk: entityPk,
            name: name
          }
        }
        this.sales.forEach(sale => {
          let entityPk = sale.company_pk
          if ((entityPk in tmpDict) === false) {
            addKey(entityPk, sale.company_name)
          }
          tmpDict[entityPk].data.sales.push(sale)
        })
        return Object.values(tmpDict)
      },
      companyDisableState () {
        // !actived_on && disabled
        let disableUser = 0
        let activatedUser = 0
        if (this.users && this.users.length > 0) {
          this.users.forEach(user => {
            if (user.disabled === true || user.activated_on === null || user.activated_on === '') disableUser += 1
            if (user.activated_on !== null && user.activated_on !== '') activatedUser += 1
          })
          if (disableUser === this.users.length || activatedUser === 0) {
            return true
          } else {
            return false
          }
        } else {
          return true
        }
      },
      lastUsersConnection () {
        let userLastConnexion = []
        if (this.users) {
          this.users.forEach(user => {
            if (user.last_login_at !== null) userLastConnexion.push(user.last_login_at)
          })
          return userLastConnexion[userLastConnexion.length - 1]
        }
        return null
      },
      statsEnabled () {
        return JSON.parse(this.$configs.STATS_ENABLED) === 1
      },
      createdOnTooltip () {
        let date = moment(this.company.created_on).format('DD/MM/YYYY')
        return `Créée le ${date}`
      },
      panelComponent () {
        const components = {
          [PANELS.USER]: 'UserPanel',
          [PANELS.OPTION]: 'OptionPanel',
          [PANELS.ACCESS]: 'CompanyAccessPanel',
          [PANELS.STATS]: 'StatsPanel',
          [PANELS.VENTES]: 'VentePanel'
        }
        return components[this.panel]
      },
      companyPk () {
        return this.$route.params.id
      },
      entityPk () {
        return this.companyPk
      },
      salesStats () {
        if (this.sales && this.companyPk) {
          return this.computedSalesData(this.sales, this.companyPk)
        }
        return null
      },
      previousSalesStats () {
        if (this.previousSales && this.companyPk) {
          return this.computedSalesData(this.previousSales, this.companyPk)
        }
        return null
      }
    },
    methods: {
      openGoogleMap (address) {
        let fullAddress
        if (address.line1) {
          let line1Convert = address.line1.split(' ')
          let convertedLine1 = ''
          line1Convert.forEach(element => {
            convertedLine1 += element + '+'
          })
          fullAddress = convertedLine1 + address.zip_code + '+' + address.city
        }
        if (address.line2) {
          let line2Convert = address.line2.split(' ')
          let convertedLine2 = ''
          line2Convert.forEach(element => {
            convertedLine2 += element + '+'
          })
          fullAddress = convertedLine2 + address.zip_code + '+' + address.city
        }
        let url = 'https://www.google.com/maps/search/?api=1&query=' + fullAddress
        window.open(url)
      },
      getUsers () {
        this.loading = true
        let query = {
          company: this.companyPk,
          sort: 'last_login_at:asc'
        }
        this.$api.userService.get(query).then(r => {
          this.loading = false
          this.users = r.data.items
        })
      },
      changePanel (panel) {
        this.panel = panel
      },
      deleteCompany () {
        this.$swal({
          title: 'Suppression',
          text: 'Êtes-vous sur de vouloir supprimer cette société et ses utilisateurs ?',
          confirmButtonText: 'Supprimer'
        }).then(r => {
          if (r.value) {
            this.$api.companyService.delete(this.companyPk)
              .then(res => {
                this.getCompaniesStore()
                this.$router.push({name: 'CompanyList'})
              })
          }
        })
      },
      getCompaniesStore (page = 1) {
        this.$api.companyService.get().then(r => {
          this.$store.commit('setCompanies', {companies: r.data})
          if (r.data.page < r.data.pages) {
            page++
            this.getCompanies(page)
          }
        })
      },
      disableCompany () {
        this.$swal({
          title: 'Suppression',
          text: `Êtes-vous sur de vouloir ${this.company.disabled ? 'activer' : 'désactiver'} cette société et ses utilisateurs ?`,
          confirmButtonText: `${this.company.disabled ? 'Activer' : 'Désactiver'}`
        }).then(r => {
          if (r.value) {
            this.company.disabled = !this.company.disabled
            this.$api.companyService.put(this.company.pk, this.company).then(res => {
              this.$notify({
                type: 'is-warning',
                text: `Société ${this.company.disabled ? 'désactivée' : 'activée'}`
              })
            })
          }
        })
      },
      getCompany () {
        this.loading = true
        this.$api.companyService.getOne(this.companyPk).then(res => {
          this.loading = false
          this.company = res.data
          this.getCompanyStatsData(this.company)
        })
      },
      getAccessesCount () {
        let now = moment()
        let query = {
          per_page: 1,
          start_date__lte: now.format('DD/MM/YYYY'),
          end_date__gte: now.format('DD/MM/YYYY'),
          company: this.companyPk
        }
        this.$api.accessService.get(query).then(r => {
          this.accessesCount = r.data.total
        })
      },
      getSalesCount () {
        let query = {
          per_page: 100,
          company: this.companyPk,
          status: ['sold', 'reserved']
        }
        this.$api.advService.get(query).then(r => {
          this.salesAll = r.data.items
          this.salesCount = r.data.total
        })
      },
      getCompanyStatsData (c) {
        let params = {
          startDate: this.startDate,
          endDate: this.endDate,
          entityKey: 'COMPANY',
          entityPk: this.entityPk
        }
        let callbacks = []
        let fn1 = (callback) => {
          this.getEntitySalesStats(params)
            .then(res => {
              this.sales = res.data.sales
              this.previousSales = res.data.previous_sales
              callback(null, res.data)
            })
        }
        callbacks.push(fn1)
        parallel(callbacks, (err, res) => {
          if (err) {
            console.log(err)
          }
          this.loadingStats = false
        })
      },
      initComponent () {
        this.panel = PANELS.USER
        this.startDate = moment().startOf('year')
        this.endDate = moment().endOf('year')
        this.getCompany()
        this.getUsers()
        this.getAccessesCount()
        this.getSalesCount()
      }
    },
    created () {
      this.initComponent()
      /*
      let statsCallback = (data) => {
        this.salesStatsData = this.computedSalesData(data.sales)
        this.previousSalesStatsData = this.computedSalesData(data.previous_sales)
      }
      this.getEntitySalesStats(this.entityKey, this.entityPk, statsCallback)
      */
    },
    mounted () {
      // Modification du panel par defaut
      if (this.statsEnabled && this.isAdmin) {
        this.panel = PANELS.STATS // Par defaut le panel STATS (si les conditions sont réunis)
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/company/CompanyVue.vue